import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { WidgetType } from '../constants';
import { Dashboard, ContentFilters } from '../models/core.models';

import { activateFilters } from '../actions/feed.actions';
import * as widgetActions from '../actions/widget.actions';

import {
  getContentFilters,
  selectCurrentObject,
  selectCurrentObjectCacheEnabled,
  selectWidgetAllPages,
  selectWidgetsLanguageId,
} from '../reducers';

import { WidgetService } from './widget.service';

import {
  tap,
  map,
  scan,
  take,
  Subject,
  finalize,
  interval,
  startWith,
  takeUntil,
  takeWhile,
  firstValueFrom,
  BehaviorSubject,
  distinctUntilChanged,
} from 'rxjs';

// ! 'normal' mode is also called 'edit' mode - when we are on certain Page
type DashboardMode = 'normal' | 'presentation';

@Injectable()
export class DashboardAutoRefreshManager {
  private secondsLeftSubject = new BehaviorSubject<number | null>(null);
  secondsLeft$ = this.secondsLeftSubject.asObservable();
  private cacheEnabledSubject = new BehaviorSubject<boolean>(false);
  cacheEnabled$ = this.cacheEnabledSubject.asObservable();

  private stop$ = new Subject<void>();
  private currentMode: DashboardMode = 'normal';
  private isRunning = false;
  private settingsSubscription: any = null;

  constructor(
    private store: Store,
    private destroyRef: DestroyRef,
    private widgetService: WidgetService,
  ) {
    this.store
      .select(selectCurrentObjectCacheEnabled)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((cacheEnabled) => {
          this.cacheEnabledSubject.next(cacheEnabled);
        }),
      )
      .subscribe();
  }

  setNormalMode(): void {
    this.currentMode = 'normal';
  }

  setPresentationMode(): void {
    this.currentMode = 'presentation';
  }

  startAutoRefresh(): void {
    if (this.settingsSubscription) {
      console.warn('[Auto-reload] Already running, skipping start');
      return;
    }

    // * Monitor dashboard settings continuously
    this.settingsSubscription = this.store
      .select(selectCurrentObject)
      .pipe(
        map((dashboard) => ({
          is_auto_refresh: (dashboard as Dashboard)?.is_auto_refresh || false,
          auto_refresh_seconds:
            (dashboard as Dashboard)?.auto_refresh_seconds || 0,
        })),
        distinctUntilChanged(
          (a, b) =>
            a.is_auto_refresh === b.is_auto_refresh &&
            a.auto_refresh_seconds === b.auto_refresh_seconds,
        ),
        tap((settings) => {
          // * Setting Changed

          this.stopCountdown();

          // * Start new countdown if enabled
          if (settings.is_auto_refresh && settings.auto_refresh_seconds > 0) {
            this.startCountdown(settings.auto_refresh_seconds);
          } else {
            this.secondsLeftSubject.next(null);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private startCountdown(refreshSeconds: number): void {
    this.isRunning = true;

    const countdown$ = (seconds: number) =>
      interval(1000).pipe(
        startWith(0),
        scan((acc) => acc - 1, seconds),
        takeWhile((s) => s > 0, true), // * inclusive: emit 0 before completing
        tap((s) => this.secondsLeftSubject.next(s)),
        finalize(() => {
          // ! Only refresh if countdown completed naturally (not cancelled)
          // ! fixes reload when auto-refresh is stopped
          if (this.isRunning) {
            this.refreshWidgets();
          }
        }),
      );

    // ! recursive Countdown
    const runCountdown = () => {
      countdown$(refreshSeconds)
        .pipe(takeUntil(this.stop$))
        .subscribe({
          complete: () => {
            // ! check if still running before starting next cycle
            if (!this.isRunning) {
              return;
            }

            // ! reset Countdown display and start next cycle
            this.secondsLeftSubject.next(refreshSeconds);

            // ! setTimeout to break synchronous recursion
            setTimeout(() => {
              if (this.isRunning) {
                runCountdown();
              }
            }, 0);
          },
        });
    };

    runCountdown();
  }

  private stopCountdown(): void {
    if (this.isRunning) {
      this.isRunning = false;
      this.stop$.next();
      this.stop$ = new Subject<void>(); // Reset for next countdown
    }
  }

  stopAutoRefresh(): void {
    this.stopCountdown();
    this.secondsLeftSubject.next(null);

    // * Unsubscribe from settings monitoring
    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
      this.settingsSubscription = null;
    }
  }

  private refreshWidgets(): void {
    if (this.currentMode === 'presentation') {
      this.refreshPresentationMode();
    } else {
      this.refreshNormalMode();
    }
  }

  private refreshNormalMode(): void {
    // * Reload current page widgets (with is_loaded check via activateFilters)
    this.store.dispatch(activateFilters());
  }

  private async refreshPresentationMode(): Promise<void> {
    try {
      // * 1. reload underlying Page Widgets (with is_loaded check)
      this.store.dispatch(activateFilters());

      // * 2. Reload all presentation widgets (no is_loaded check - eager)
      const [widgetsMap, contentFilters, languageId, cacheEnabled] =
        await Promise.all([
          firstValueFrom(this.store.select(selectWidgetAllPages).pipe(take(1))),
          firstValueFrom(this.store.select(getContentFilters).pipe(take(1))),
          firstValueFrom(
            this.store.select(selectWidgetsLanguageId).pipe(take(1)),
          ),
          firstValueFrom(
            this.store.select(selectCurrentObjectCacheEnabled).pipe(take(1)),
          ),
        ]);

      const widgets = Array.from(widgetsMap.values()).filter(
        (w) => w.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER,
      );

      if (widgets.length === 0) {
        console.warn('[Auto-reload] No widgets found in presentation mode!');
        return;
      }

      // * Set loading state for all widgets BEFORE fetching
      const widgetIds = widgets.map((w) => w.id);

      this.store.dispatch(widgetActions.startBatchLoadWidgets({ widgetIds }));

      // * Fetch all widgets data in parallel
      const results = await Promise.all(
        widgets.map((widget) =>
          firstValueFrom(
            this.widgetService.getWidgetData(
              widget.id,
              contentFilters as ContentFilters,
              languageId as number,
              cacheEnabled,
            ),
          ).catch((err) => {
            console.warn(
              `[Auto-reload] Widget ${widget.id} (${widget.title}) failed:`,
              err,
            );
            return null;
          }),
        ),
      );

      // * filter successful updates and dispatch batch
      const successfulUpdates = widgets
        .map((widget, i) => ({ widgetId: widget.id, data: results[i] }))
        .filter((update) => update.data !== null);

      if (successfulUpdates.length > 0) {
        this.store.dispatch(
          widgetActions.batchUpdateWidgetData({ updates: successfulUpdates }),
        );
      }
    } catch (error) {
      console.warn('[Auto-reload] Presentation mode refresh failed:', error);
    }
  }
}
